import { useCallback, useEffect, useRef, useState } from 'react';
import type { Paged } from '../api';

/**
 * 서버 페이지네이션 목록(`{items,total,limit,offset}`)을 화면에 붙이는 공통 훅(2026-09-15).
 * 접속 이력·감사 로그가 같은 흐름(불러오기 → 쪽 이동 → 새로고침 → 오류 표시)을 쓰므로 한 곳에 둔다.
 *
 * @param fetchPage offset 을 받아 한 쪽을 가져오는 함수. **필터가 바뀌면 새 함수가 되도록 useCallback 으로 감쌀 것** —
 *   함수가 바뀌면 첫 쪽부터 다시 불러온다(필터를 바꿨는데 3쪽에 머물면 "결과가 없다" 로 오해한다).
 */
export function usePagedList<T>(fetchPage: (offset: number) => Promise<Paged<T>>) {
  const [page, setPage] = useState<Paged<T> | null>(null);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // 늦게 도착한 옛 응답이 새 응답을 덮지 않게 요청마다 번호를 매긴다(필터를 빠르게 바꿀 때 실제로 생긴다).
  const seq = useRef(0);

  const load = useCallback(
    async (off: number) => {
      const my = ++seq.current;
      setLoading(true);
      setError('');
      try {
        const p = await fetchPage(off);
        if (my === seq.current) {
          setPage(p);
          setOffset(off);
        }
      } catch (e) {
        if (my === seq.current) {
          setError((e as Error).message);
        }
      } finally {
        if (my === seq.current) {
          setLoading(false);
        }
      }
    },
    [fetchPage],
  );

  useEffect(() => {
    void load(0);
  }, [load]);

  return {
    items: page?.items ?? [],
    total: page?.total ?? 0,
    limit: page?.limit ?? 0,
    offset,
    loading,
    error,
    /** 다른 쪽으로 이동. */
    goTo: (off: number) => void load(off),
    /** 지금 쪽을 다시 불러온다. */
    reload: () => void load(offset),
  };
}
